/**
 * Common types, enums, and base types for the Livestock Management System
 */
// ============================================
// ENUMS - Match DB enum naming
// ============================================
export var LivestockStatus;
(function (LivestockStatus) {
    LivestockStatus["ACTIVE"] = "ACTIVE";
    LivestockStatus["SOLD"] = "SOLD";
    LivestockStatus["DECEASED"] = "DECEASED";
    LivestockStatus["TRANSFERRED"] = "TRANSFERRED";
})(LivestockStatus || (LivestockStatus = {}));
export var AnimalSex;
(function (AnimalSex) {
    AnimalSex["MALE"] = "MALE";
    AnimalSex["FEMALE"] = "FEMALE";
})(AnimalSex || (AnimalSex = {}));
export var HerdPurpose;
(function (HerdPurpose) {
    HerdPurpose["BREEDING"] = "BREEDING";
    HerdPurpose["FATTENING"] = "FATTENING";
    HerdPurpose["DAIRY"] = "DAIRY";
    HerdPurpose["MIXED"] = "MIXED";
    HerdPurpose["REPLACEMENT"] = "REPLACEMENT";
})(HerdPurpose || (HerdPurpose = {}));
export var LivestockOperationType;
(function (LivestockOperationType) {
    // Health
    LivestockOperationType["VACCINATION"] = "VACCINATION";
    LivestockOperationType["TREATMENT"] = "TREATMENT";
    LivestockOperationType["DISEASE"] = "DISEASE";
    LivestockOperationType["MORTALITY"] = "MORTALITY";
    // Nutrition
    LivestockOperationType["FEEDING_PLAN"] = "FEEDING_PLAN";
    LivestockOperationType["SUPPLEMENTATION"] = "SUPPLEMENTATION";
    // Pasture
    LivestockOperationType["GRAZING_ROTATION"] = "GRAZING_ROTATION";
    // Management
    LivestockOperationType["WEANING"] = "WEANING";
    LivestockOperationType["CASTRATION"] = "CASTRATION";
    LivestockOperationType["DEHORNING"] = "DEHORNING";
    LivestockOperationType["IDENTIFICATION"] = "IDENTIFICATION";
    LivestockOperationType["HOOF_TRIMMING"] = "HOOF_TRIMMING";
    // Hygiene
    LivestockOperationType["CLEANING"] = "CLEANING";
    LivestockOperationType["PEST_CONTROL"] = "PEST_CONTROL";
    // Reproduction
    LivestockOperationType["ARTIFICIAL_INSEMINATION"] = "ARTIFICIAL_INSEMINATION";
    // Commerce
    LivestockOperationType["SALE"] = "SALE";
    // Other
    LivestockOperationType["OTHER"] = "OTHER";
    // Reproduction (Phase 3) — appended after OTHER to mirror the pgEnum, which
    // only supports additive `ALTER TYPE ... ADD VALUE` at the tail.
    // Note: CALVING also exists in LivestockMeasurementType (legacy) — nothing
    // may infer record kind from the type value alone (AnimalRecordCondition
    // carries an explicit `kind` for exactly this reason).
    LivestockOperationType["HEAT_DETECTION"] = "HEAT_DETECTION";
    LivestockOperationType["NATURAL_BREEDING"] = "NATURAL_BREEDING";
    LivestockOperationType["PREGNANCY_DIAGNOSIS"] = "PREGNANCY_DIAGNOSIS";
    LivestockOperationType["ABORTION"] = "ABORTION";
    LivestockOperationType["CALVING"] = "CALVING";
})(LivestockOperationType || (LivestockOperationType = {}));
export var LivestockSalesChannel;
(function (LivestockSalesChannel) {
    LivestockSalesChannel["DIRECT"] = "DIRECT";
    LivestockSalesChannel["AUCTION"] = "AUCTION";
    LivestockSalesChannel["MARKETPLACE"] = "MARKETPLACE";
    LivestockSalesChannel["OTHER"] = "OTHER";
})(LivestockSalesChannel || (LivestockSalesChannel = {}));
export var LivestockPricingModel;
(function (LivestockPricingModel) {
    LivestockPricingModel["PER_HEAD"] = "PER_HEAD";
    LivestockPricingModel["PER_KG"] = "PER_KG";
    LivestockPricingModel["PER_ARROBA"] = "PER_ARROBA";
    LivestockPricingModel["LOT_TOTAL"] = "LOT_TOTAL";
})(LivestockPricingModel || (LivestockPricingModel = {}));
/**
 * Weight used to settle a livestock sale.
 *
 * `LIVE` is farm-to-farm / bezerro / recria (@ or R$/kg of live weight).
 * Carcass bases are ao gancho (packer): 1 @ = 15 kg of carcass. Estimated
 * carcass keeps the yield assumption; actual carcass is the romaneio weight.
 */
export var LivestockSaleWeightBasis;
(function (LivestockSaleWeightBasis) {
    LivestockSaleWeightBasis["LIVE"] = "LIVE";
    LivestockSaleWeightBasis["CARCASS_ACTUAL"] = "CARCASS_ACTUAL";
    LivestockSaleWeightBasis["CARCASS_ESTIMATED"] = "CARCASS_ESTIMATED";
})(LivestockSaleWeightBasis || (LivestockSaleWeightBasis = {}));
export var LivestockMeasurementType;
(function (LivestockMeasurementType) {
    LivestockMeasurementType["WEIGHT"] = "WEIGHT";
    LivestockMeasurementType["BODY_CONDITION_SCORE"] = "BODY_CONDITION_SCORE";
    LivestockMeasurementType["HIP_HEIGHT"] = "HIP_HEIGHT";
    LivestockMeasurementType["SCROTAL_CIRCUMFERENCE"] = "SCROTAL_CIRCUMFERENCE";
    LivestockMeasurementType["BODY_TEMPERATURE"] = "BODY_TEMPERATURE";
    LivestockMeasurementType["PREGNANCY_CHECK"] = "PREGNANCY_CHECK";
    LivestockMeasurementType["CALVING"] = "CALVING";
    LivestockMeasurementType["BLOOD_EXAM"] = "BLOOD_EXAM";
    LivestockMeasurementType["GENERAL_EXAMINATION"] = "GENERAL_EXAMINATION";
})(LivestockMeasurementType || (LivestockMeasurementType = {}));
export var HornStatus;
(function (HornStatus) {
    HornStatus["HORNED"] = "HORNED";
    HornStatus["POLLED"] = "POLLED";
    HornStatus["DEHORNED"] = "DEHORNED";
    HornStatus["SCURRED"] = "SCURRED";
})(HornStatus || (HornStatus = {}));
export var ManagementSystem;
(function (ManagementSystem) {
    ManagementSystem["EXTENSIVE"] = "EXTENSIVE";
    ManagementSystem["SEMI_INTENSIVE"] = "SEMI_INTENSIVE";
    ManagementSystem["INTENSIVE"] = "INTENSIVE";
    ManagementSystem["CONFINEMENT"] = "CONFINEMENT";
})(ManagementSystem || (ManagementSystem = {}));
// ============================================
// BASE/UTILITY TYPES
// ============================================
// Note: DateRangeFilter, AuditInfo, and BaseEntity are exported from the root Common.ts
// They should be imported from "@packages/shared-types" directly, not from this module
//# sourceMappingURL=Common.js.map